import React, { ReactElement, useRef, useState } from "react";
import { MenuItem } from "@material-ui/core";
import { useSnackbar } from "notistack";

import { RemoveImageLogo } from "pages/location/components/add-location/styles";
import {
  FieldContainer,
  InputLabelText,
  ModelDropdown,
  UploadInput,
  PdfPreviewWrapper,
  PdfPreviewLabel,
  PdfPreviewFrame,
  EmptyPreviewText,
  UploadSectionsRow,
  ClickablePdfPreview,
  PdfPreviewContainer,
} from "pages/management/warranty/styles";
import PdfPreviewModal from "pages/management/warranty/components/pdf-preview-modal";
import { WARRANTY_CAR_MODELS } from "constants/warranty-constants";

interface PdfUploadSectionProps {
  label: string;
  pdfUrl?: string;
  onUpload: (dataUrl: string) => void;
  onRemove: () => void;
  disabled?: boolean;
  disabledHint?: string;
}

function PdfUploadSection({
  label,
  pdfUrl,
  onUpload,
  onRemove,
  disabled = false,
  disabledHint,
}: PdfUploadSectionProps): ReactElement {
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [expandOpen, setExpandOpen] = useState(false);
  const hasPdf = Boolean(pdfUrl?.trim());

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    if (disabled) {
      enqueueSnackbar(disabledHint ?? "Upload is disabled", { variant: "warning" });
      e.target.value = "";
      return;
    }
    if (file.type !== "application/pdf") {
      enqueueSnackbar("Please upload a PDF file", { variant: "error" });
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      onUpload(reader.result as string);
      enqueueSnackbar("Warranty PDF uploaded successfully", {
        variant: "success",
      });
    };
    reader.onerror = () => {
      enqueueSnackbar("Failed to read PDF file", { variant: "error" });
    };
    e.target.value = "";
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemove();
  };

  return (
    <FieldContainer>
      <InputLabelText>{label}</InputLabelText>
      <UploadInput
        ref={fileInputRef}
        type="file"
        accept="application/pdf,.pdf"
        onChange={handleFileChange}
        disabled={disabled}
      />
      {disabled && disabledHint ? (
        <EmptyPreviewText>{disabledHint}</EmptyPreviewText>
      ) : null}
      {hasPdf ? (
        <>
          <PdfPreviewWrapper>
            <PdfPreviewLabel>PDF preview</PdfPreviewLabel>
            <PdfPreviewContainer>
              <ClickablePdfPreview
                role="button"
                tabIndex={0}
                onClick={() => setExpandOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setExpandOpen(true);
                  }
                }}
              >
                <PdfPreviewFrame src={pdfUrl} title={`${label} preview`} />
              </ClickablePdfPreview>
              <RemoveImageLogo onClick={handleRemove} />
            </PdfPreviewContainer>
          </PdfPreviewWrapper>
          <PdfPreviewModal
            open={expandOpen}
            onClose={() => setExpandOpen(false)}
            pdfUrl={pdfUrl}
            title={`${label} expanded view`}
          />
        </>
      ) : !disabled ? (
        <EmptyPreviewText>Upload a PDF to preview it here.</EmptyPreviewText>
      ) : null}
    </FieldContainer>
  );
}

interface Props {
  showModelDropdown?: boolean;
  selectedModel?: string;
  onModelChange?: (model: string) => void;
  standardPdfUrl?: string;
  extendedPdfUrl?: string;
  onStandardUpload?: (dataUrl: string) => void;
  onExtendedUpload?: (dataUrl: string) => void;
  onStandardRemove?: () => void;
  onExtendedRemove?: () => void;
}

function WarrantyPdfPanel({
  showModelDropdown = false,
  selectedModel = "",
  onModelChange,
  standardPdfUrl,
  extendedPdfUrl,
  onStandardUpload,
  onExtendedUpload,
  onStandardRemove,
  onExtendedRemove,
}: Props): ReactElement {
  const requiresModel = showModelDropdown;
  const uploadDisabled = requiresModel && !selectedModel;
  const disabledHint = requiresModel
    ? "Select a car model before uploading a PDF."
    : undefined;

  const handleModelChange = (
    e: React.ChangeEvent<{ name?: string; value: unknown }>
  ) => {
    onModelChange?.(String(e.target.value ?? ""));
  };

  return (
    <>
      {showModelDropdown ? (
        <FieldContainer>
          <InputLabelText>Select a car model</InputLabelText>
          <ModelDropdown
            onChange={handleModelChange}
            variant="outlined"
            value={selectedModel}
            displayEmpty
            MenuProps={{
              anchorOrigin: { vertical: "bottom", horizontal: "left" },
              getContentAnchorEl: null,
            }}
          >
            <MenuItem value="" disabled>
              Select a car model
            </MenuItem>
            {WARRANTY_CAR_MODELS.map((model) => (
              <MenuItem key={model} value={model}>
                {model}
              </MenuItem>
            ))}
          </ModelDropdown>
        </FieldContainer>
      ) : null}

      <UploadSectionsRow>
        <PdfUploadSection
          label="Upload standard Warranty PDF"
          pdfUrl={standardPdfUrl}
          onUpload={(dataUrl) => onStandardUpload?.(dataUrl)}
          onRemove={() => onStandardRemove?.()}
          disabled={uploadDisabled}
          disabledHint={disabledHint}
        />
        <PdfUploadSection
          label="Upload extended Warranty PDF"
          pdfUrl={extendedPdfUrl}
          onUpload={(dataUrl) => onExtendedUpload?.(dataUrl)}
          onRemove={() => onExtendedRemove?.()}
          disabled={uploadDisabled}
          disabledHint={disabledHint}
        />
      </UploadSectionsRow>
    </>
  );
}

export default WarrantyPdfPanel;
